import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/*
 * Icarus D&D Redesign (design_handoff_icarus_dnd/README.md, "Sonstiges"):
 * Buttons 10–12px Radius; Primär = Grün mit weichem CTA-Schatten, Hover
 * dunkler; Outline = weiß mit Hairline-Rahmen und leichter Tönung auf
 * Hover (kein Akzent-Fill mehr); Primär-CTAs in 600.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary font-semibold text-primary-foreground hover:bg-primary-hover icarus-cta-shadow",
        destructive: "bg-destructive font-semibold text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-[oklch(0.88_0.005_250)] bg-surface text-[oklch(0.42_0.02_250)] hover:bg-[oklch(0.97_0.004_250)] hover:text-ink",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "text-ink-secondary hover:bg-[oklch(0.97_0.004_250)] hover:text-ink",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // BFSG/WCAG 2.5.5 (Target Size): mindestens 44×44 CSS-Pixel für
        // alle primären Touch-Targets. Default und Icon-Variante wurden
        // von 40 px (`h-10`) auf 44 px (`h-11`) angehoben. `sm` bleibt
        // für dichte Tabellen-Toolbars bei 36 px — dort sind die Tasten
        // immer Sekundär-Aktionen mit ausreichendem Spacing.
        default: "h-11 px-5 py-2",
        sm: "h-9 rounded-[10px] px-4",
        lg: "h-12 rounded-xl px-7",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
